import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Camera, ImagePlus, Loader2, RefreshCw, X } from "lucide-react";

import {
  BURST_FRAMES,
  drawDownscaled,
  BURST_INTERVAL_MS,
  UPLOAD_QUALITY,
  dominantProblem,
  measureFrame,
  prepareUploadedFrames,
  selectBurstFrames,
  uploadSize,
  type FrameProblem,
  type FrameQuality,
} from "../../utils/plateCapture";

/**
 * Photographing a container's door plate.
 *
 * A live camera by preference, with the phone's picker behind it, and the live
 * path is where the accuracy comes from. Picking a file gives you one frame,
 * chosen by somebody who cannot tell from a thumbnail whether the stencil is
 * sharp, and the first anybody learns it was not is a failed reading half a
 * minute later. A live view lets three things happen that a picker cannot:
 *
 *   - a guide frame, so the plate is filled and square rather than distant and
 *     angled — the single biggest lever on whether any of this works;
 *   - a burst, so the reader gets several independent looks at the same door.
 *     Recognition errors are largely independent between frames, so a per-field
 *     vote across them removes a whole class of mistake that no amount of work
 *     on one frame would;
 *   - a sharpness check before the upload, so a blurred frame is caught here
 *     instead of after a slow upload from a yard with poor signal.
 *
 * The picker is offered anyway, for two reasons that are not the same.
 *
 * In development the live camera is often simply unavailable — it needs a
 * secure context and a rear camera, and neither is a given on a build machine.
 * Being able to feed the pipeline a folder of container photographs is the
 * difference between testing this feature and testing the half of it that runs
 * on a server.
 *
 * In a yard it is a fallback that was missing. A camera can be denied by device
 * policy, blocked on a plain-HTTP intranet, or behind a cracked lens, and until
 * now those drivers had exactly one option: type all five figures by hand. They
 * can now photograph the door with the phone's own camera app and pick the
 * result.
 *
 * Both paths run the identical pipeline — decode, measure, rank, downscale — so
 * a blurred photograph is caught either way, and what is exercised in
 * development is the code that ships.
 *
 * The component only captures. It hands frames upward and says nothing about
 * what they mean — the verdict comes from the platform, which is the only side
 * that holds the check digit, the plate's arithmetic and the unit columns.
 */

interface PlateCaptureProps {
  /** Called with the frames worth reading, best first. */
  onCapture: (frames: File[]) => void;
  onClose: () => void;
  /** True while the platform is reading what was sent. */
  busy?: boolean;
}

type Phase = "starting" | "ready" | "capturing" | "denied" | "unsupported";

export default function PlateCapture({
  onCapture,
  onClose,
  busy = false,
}: PlateCaptureProps) {
  const { t } = useTranslation("containerOrders");

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const [phase, setPhase] = useState<Phase>("starting");
  /**
   * The photograph that was taken, shown while the platform reads it.
   *
   * The camera is stopped the moment a frame is chosen — a live preview costs
   * battery and a light on the back of the phone, and there is nothing left to
   * aim at. But stopping it leaves the `<video>` element showing black, and a
   * black screen for the several seconds a read takes looks like a crash rather
   * than like work. This is the frame that was actually sent, so the driver can
   * see what the reader is looking at while it looks.
   */
  const [preview, setPreview] = useState<string | null>(null);
  const [problem, setProblem] = useState<FrameProblem | null>(null);
  /** Live sharpness, sampled from the preview — the steadiness meter. */
  const [live, setLive] = useState<FrameQuality | null>(null);

  /**
   * Stop the camera.
   *
   * Held in a ref and called from several places because a camera left running
   * is a light on the back of somebody's phone and a flat battery. React's
   * cleanup is the last line of defence, not the only one.
   */
  /**
   * Hold the taken photograph on screen, and release the previous one.
   *
   * Object URLs are not garbage-collected with the blob behind them, so every
   * one handed out has to be handed back or the page leaks a whole photograph
   * per retake.
   */
  const showPreview = useCallback((frame: File | null) => {
    setPreview((current) => {
      if (current) URL.revokeObjectURL(current);
      return frame ? URL.createObjectURL(frame) : null;
    });
  }, []);

  useEffect(
    () => () => {
      setPreview((current) => {
        if (current) URL.revokeObjectURL(current);
        return null;
      });
    },
    [],
  );

  const stop = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function start() {
      if (!navigator.mediaDevices?.getUserMedia) {
        setPhase("unsupported");
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            // The rear camera, and as much resolution as the device will give.
            // `ideal` rather than `exact` throughout: a constraint the hardware
            // cannot meet fails the whole request when it is exact, and a
            // slightly smaller frame is much better than no camera at all.
            facingMode: { ideal: "environment" },
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        setPhase("ready");
      } catch {
        // Denial and absence are one branch on purpose: from here they are the
        // same situation — no camera — and the way out of both is the same.
        if (!cancelled) setPhase("denied");
      }
    }

    start();
    return () => {
      cancelled = true;
      stop();
    };
  }, [stop]);

  /** Grab the current preview frame into the working canvas. */
  const grab = useCallback((longEdge?: number) => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !video.videoWidth) return null;

    const { width, height } = longEdge
      ? uploadSize(video.videoWidth, video.videoHeight, longEdge)
      : { width: video.videoWidth, height: video.videoHeight };

    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d", { willReadFrequently: true });
    if (!context) return null;
    context.drawImage(video, 0, 0, width, height);
    return { canvas, context, width, height };
  }, []);

  /**
   * Sample the preview a few times a second, for the steadiness meter.
   *
   * Measured on a small copy rather than the full frame: this runs continuously
   * while somebody lines up a shot, and walking two million pixels several
   * times a second would heat the phone and stutter the preview. Sharpness is a
   * relative measure here — it only has to say "steadier than a moment ago" —
   * so the smaller sample is no loss.
   */
  useEffect(() => {
    if (phase !== "ready") return;
    const timer = window.setInterval(() => {
      const grabbed = grab(320);
      if (!grabbed) return;
      const { context, width, height } = grabbed;
      try {
        setLive(measureFrame(context.getImageData(0, 0, width, height)));
      } catch {
        // A tainted canvas or a frame arriving mid-teardown. The meter is a
        // convenience; losing it must never take the camera down with it.
      }
    }, 400);
    return () => window.clearInterval(timer);
  }, [phase, grab]);

  /**
   * Take the burst.
   *
   * Frames are measured as they are taken and chosen afterwards, so the
   * decision is made across the whole set rather than frame by frame. That is
   * what lets the fallback work: if nothing cleared the bar, the best of a bad
   * set is still sent, because the gate is a heuristic and the recogniser is
   * often perfectly capable of reading what it rejected.
   */
  const capture = useCallback(async () => {
    setPhase("capturing");
    setProblem(null);
    showPreview(null);

    const taken: Array<{ frame: File; quality: FrameQuality }> = [];

    for (let i = 0; i < BURST_FRAMES; i += 1) {
      const grabbed = grab();
      if (grabbed) {
        const { canvas, context, width, height } = grabbed;

        let quality: FrameQuality;
        try {
          quality = measureFrame(context.getImageData(0, 0, width, height));
        } catch {
          quality = { ok: true, sharpness: 0, brightness: 0 };
        }

        // Measured at full size and *sent* downscaled: the sharpness check
        // wants every pixel the sensor gave, and the upload does not — a yard
        // connection is usually the slowest part of this whole feature.
        const upload = uploadSize(width, height);
        const out = document.createElement("canvas");
        out.width = upload.width;
        out.height = upload.height;
        const outContext = out.getContext("2d");
        if (outContext) {
          await drawDownscaled(
            outContext,
            canvas,
            width,
            height,
            upload.width,
            upload.height,
          );
        }

        const blob = await new Promise<Blob | null>((resolve) =>
          out.toBlob(resolve, "image/jpeg", UPLOAD_QUALITY),
        );
        if (blob) {
          taken.push({
            frame: new File([blob], `plate-${i + 1}.jpg`, { type: "image/jpeg" }),
            quality,
          });
        }
      }
      if (i < BURST_FRAMES - 1) {
        await new Promise((resolve) => setTimeout(resolve, BURST_INTERVAL_MS));
      }
    }

    const { selected, rejected } = selectBurstFrames(taken);

    if (!selected.length) {
      setProblem("blurred");
      setPhase("ready");
      return;
    }

    // Said even when a frame was sent: one that only just cleared the bar is
    // not necessarily good, and the driver is better off knowing before the
    // answer comes back than after.
    if (rejected.length >= selected.length) {
      setProblem(dominantProblem(rejected));
    }

    showPreview(selected[0]);
    stop();
    onCapture(selected);
  }, [grab, onCapture, showPreview, stop]);

  /**
   * Frames chosen from the gallery rather than taken here.
   *
   * Runs the identical pipeline — decode, measure, rank, downscale — so a
   * blurred photograph is caught the same way and what is exercised in
   * development is the code that ships. The only difference is where the pixels
   * came from.
   */
  const pick = useCallback(
    async (files: FileList | null) => {
      if (!files?.length) return;
      setPhase("capturing");
      setProblem(null);
      showPreview(null);

      const { selected, rejected, undecodable } = await prepareUploadedFrames(
        Array.from(files),
      );

      if (!selected.length) {
        // Nothing survived decoding. Almost always a HEIC this browser has no
        // decoder for, which is worth saying plainly rather than reporting as a
        // blurred photograph the driver could retake forever.
        setProblem(undecodable ? "tooSmall" : "blurred");
        setPhase("ready");
        return;
      }

      if (rejected.length >= selected.length) {
        setProblem(dominantProblem(rejected));
      }

      showPreview(selected[0]);
      stop();
      onCapture(selected);
    },
    [onCapture, stop],
  );

  const steady = live?.ok ?? false;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black">
      <div className="flex items-center justify-between px-4 py-3 text-white">
        <span className="text-sm font-medium">{t("plate.capture.title")}</span>
        <button
          type="button"
          onClick={() => {
            stop();
            onClose();
          }}
          className="rounded-full p-1.5 hover:bg-white/10"
          aria-label={t("plate.capture.close")}
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="relative flex-1 overflow-hidden">
        <video
          ref={videoRef}
          playsInline
          muted
          className="h-full w-full object-cover"
        />
        {/* The working canvas never appears; it is where frames are measured
            and compressed. */}
        <canvas ref={canvasRef} className="hidden" />

        {/* Covers the stopped camera.

            Contained rather than cropped, unlike the live preview above it.
            The video is cropped because it is something to aim with and the
            edges are not where the plate is; this is something to *check*, and
            it is the frame that was actually sent. Cropping it would hide the
            part a driver most wants to see when the answer comes back wrong —
            whether the number was in the photograph at all. */}
        {preview && (
          <img
            src={preview}
            alt=""
            className="absolute inset-0 h-full w-full object-contain"
          />
        )}

        {/* Aiming furniture, and only while there is still something to aim.
            Left over the taken photograph it read as though the driver had
            failed to line a shot up that was already taken and sent. */}
        {!preview && (phase === "ready" || phase === "capturing") ? (
          <>
            {/* The guide frame, at roughly the proportions of the block that
                carries the number and the weights. It is the cheapest accuracy
                in the feature: a plate that fills this rectangle is close,
                square and in focus, and almost every hard case the reader faces
                is a photograph taken from too far away and at an angle. */}
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <div
                className={`h-[46%] w-[86%] rounded-lg border-2 transition-colors ${
                  steady ? "border-[#4E9A7C]" : "border-white/70"
                }`}
              />
            </div>

            <div className="pointer-events-none absolute inset-x-0 bottom-24 px-6 text-center">
              <p className="text-sm text-white drop-shadow">
                {t("plate.capture.aim")}
              </p>
              {/* Live, and phrased as encouragement rather than as an error:
                  this is showing while somebody is still lining the shot up,
                  and a red warning at that moment reads as a refusal. */}
              {live && !steady && (
                <p className="mt-1 text-xs text-amber-200 drop-shadow">
                  {t("plate.capture.holdStill")}
                </p>
              )}
            </div>
          </>
        ) : null}

        {phase === "starting" && (
          <div className="absolute inset-0 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-white/80" />
          </div>
        )}

        {/* Also stood down once a photograph is in hand. On a machine with no
            camera at all — a laptop, or a phone whose permission is refused —
            this panel is the whole screen, and leaving it over the picked
            photograph offers to choose a file while the one just chosen is
            being read behind it. */}
        {!preview && (phase === "denied" || phase === "unsupported") && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-8 text-center">
            <p className="text-sm text-white">
              {t(
                phase === "denied"
                  ? "plate.capture.denied"
                  : "plate.capture.unsupported",
              )}
            </p>
            {/* Picking a file is offered first here, because it is the option
                that still reads the plate. A driver whose camera is blocked by
                policy can photograph the door with the phone's own camera app
                and choose the result; being sent straight to typing five
                figures by hand should be the last resort, not the only one. */}
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="flex items-center gap-2 rounded-lg bg-white px-4 py-2 text-sm font-medium text-black"
            >
              <ImagePlus className="h-4 w-4" />
              {t("plate.capture.choosePhotos")}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="text-sm text-white/70 underline"
            >
              {t("plate.capture.enterByHand")}
            </button>
          </div>
        )}
      </div>

      <div className="space-y-2 px-6 pb-8 pt-4">
        {problem && (
          <p className="text-center text-xs text-amber-300">
            {t(`plate.capture.problem.${problem}`)}
          </p>
        )}

        <div className="flex items-center justify-center gap-8">
          {/* Secondary on purpose, and to the side of the shutter rather than
              beside it in size: the live path is the one that produces a burst
              and a guide frame, and it should stay the obvious thing to press. */}
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={phase === "capturing" || busy}
            className="flex flex-col items-center gap-1 text-white/70 disabled:opacity-40"
            aria-label={t("plate.capture.choosePhotos")}
          >
            <ImagePlus className="h-6 w-6" />
            <span className="text-[10px]">{t("plate.capture.upload")}</span>
          </button>

          <button
            type="button"
            onClick={capture}
            disabled={phase !== "ready" || busy}
            className="flex h-16 w-16 items-center justify-center rounded-full bg-white text-black disabled:opacity-40"
            aria-label={t("plate.capture.shutter")}
          >
            {phase === "capturing" || busy ? (
              <Loader2 className="h-6 w-6 animate-spin" />
            ) : problem ? (
              <RefreshCw className="h-6 w-6" />
            ) : (
              <Camera className="h-6 w-6" />
            )}
          </button>

          {/* Balances the picker on the other side of the shutter, so the
              shutter sits centred. */}
          <span className="w-6" aria-hidden />
        </div>

        {/* One file, matching the shutter: the reader looks at one frame, so
            offering to pick five only spends a yard's upload bandwidth on four
            that are discarded. The value is cleared after each pick so choosing
            the same file twice still fires. */}
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            void pick(e.target.files);
            e.target.value = "";
          }}
        />

        {/* Kept at a fixed height whether or not it says anything, so the
            shutter does not jump up the screen when a read begins. */}
        <p className="min-h-[15px] text-center text-[11px] text-white/60">
          {busy ? t("plate.capture.reading") : ""}
        </p>
      </div>
    </div>
  );
}
